import { useEffect, useRef } from "react";
import { createFileRoute, useLoaderData, useRouter } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { HappeningExperience } from "@/components/HappeningExperience";
import { RelatedHubs } from "@/components/RelatedHubs";
import { SeoItemList } from "@/components/SeoItemList";
import { getSeoHappeningItems } from "@/lib/seo.functions";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";
import { useAutoTranslate } from "@/i18n/useAutoTranslate";


export const Route = createFileRoute("/happening/")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { page?: number; r?: string; city?: string; lane?: string; q?: string } => ({
    page: Number(search.page) > 0 ? Number(search.page) : 1,
    r: typeof search.r === "string" ? search.r : undefined,
    city: typeof search.city === "string" ? search.city : undefined,
    lane: typeof search.lane === "string" ? search.lane : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  loader: async () => {
    try {
      const { items } = await getSeoHappeningItems({ data: { limit: 30 } });
      return { items };
    } catch {
      return { items: [] };
    }
  },
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? [];
    const url = "https://whatfinland.com/happening";
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "What's happening in Finland — live events",
      url,
      numberOfItems: items.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://whatfinland.com/happening/${it.slug}`,
        name: it.name,
      })),
    };
    // BreadcrumbList intentionally omitted on this layout route — leaf
    // /happening/$slug emits its own full breadcrumb and TanStack
    // concatenates scripts, which would otherwise produce a duplicate
    // BreadcrumbList on detail pages (Rich Results validation error).
    return {
      meta: [
        { title: "What's Happening in Helsinki — Live Events & Gigs" },
        {
          name: "description",
          content:
            "Hottest concerts, exhibitions and cultural moments in Helsinki, curated by what Finland Oy.",
        },
        { property: "og:title", content: "What's Happening — Helsinki" },
        {
          property: "og:description",
          content: "Tonight's concerts, this week's exhibitions, the moments worth showing up for.",
        },
        { property: "og:url", content: url },
        ...buildLocaleMeta("en"),
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks("/happening")],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(itemList) },
      ],
    };
  },
  component: RouteComponent,
  errorComponent: HappeningError,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-heading text-2xl">No Events Found</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This events page isn't available. Try the events overview instead.
      </p>
    </div>
  ),
});

function HappeningError({ error, reset }: { error: any; reset: () => void }) {
  const router = useRouter();
  const retried = useRef(false);

  // Transient loader/chunk failures on this route are common (cold start,
  // deploy swap). Self-heal once before showing anything to the visitor.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error("[/happening] render error:", error);
    if (retried.current) return;
    retried.current = true;
    const t = window.setTimeout(() => {
      router.invalidate();
      reset();
    }, 400);
    return () => window.clearTimeout(t);
  }, [error, reset, router]);

  return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-heading text-2xl">Loading Events…</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We hit a hiccup fetching what's happening. Retrying now.
      </p>
      <button
        onClick={() => {
          router.invalidate();
          reset();
        }}
        className="mt-6 rounded-full bg-foreground px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-background"
      >
        Try again
      </button>
    </div>
  );
}

function RouteComponent() {
  // `strict: false` so the /fi mirror route can reuse this component.
  const { items } = useLoaderData({ strict: false }) as any;
  const seoHeading = useAutoTranslate("All upcoming events in Finland");
  return (
    <>
      <HappeningExperience />

      <RelatedHubs current="happening" />
      <SeoItemList
        items={items}
        heading={seoHeading}
        hrefBase="/happening"
      />
    </>
  );
}
